<script setup>
import { computed, defineModel, defineProps, nextTick, ref, watch } from 'vue';
import { isEmpty } from '@/service/utils';
import ArrowSvg from '@/components/UI/ArrowSvg.vue';
import LoadingSvg from '@/components/UI/LoadingSvg.vue';

const value = defineModel();

const isVisible = ref(false);
const searchQuery = ref('');
const searchInputRef = ref(null);

const props = defineProps({
    values: {
        type: Array,
        required: true,
    },
    label: {
        type: String,
        required: true,
    },
    formatFunction: {
        type: Function,
        required: true,
    },
    isError: {
        type: Boolean,
        required: true,
        default: false,
    },
    isLoading: {
        type: Boolean,
        required: false,
        default: false,
    },
    isLoadingLabel: {
        type: String,
        required: false,
        default: '',
    },
    searchable: {
        type: Boolean,
        default: false,
    },
    searchPlaceholder: {
        type: String,
        default: 'Поиск...',
    },
});

const filteredValues = computed(() => {
    if (!props.searchable) {
        return props.values;
    }

    const query = searchQuery.value.trim().toLowerCase();
    if (!query) {
        return props.values;
    }

    return props.values.filter((item) => {
        try {
            return String(props.formatFunction(item) ?? '').toLowerCase().includes(query);
        } catch (error) {
            return false;
        }
    });
});

async function openDropdown() {
    if (props.isLoading) return;
    isVisible.value = true;

    if (props.searchable) {
        await nextTick();
        searchInputRef.value?.focus();
    }
}

function closeDropdown() {
    isVisible.value = false;
    searchQuery.value = '';
}

function selectItem(item) {
    value.value = item;
    closeDropdown();
}

watch(
    () => props.isLoading,
    (loading) => {
        if (loading) {
            closeDropdown();
        }
    },
);
</script>

<template>
    <div
        class="h-12 border rounded-sm relative flex items-center px-4"
        :class="{
            'cursor-pointer': !props.isLoading,
            'border-black': !isError && !props.isLoading,
            'border-red-600': isError && !props.isLoading,
            'border-gray-400': props.isLoading,
        }"
        @click="(e) => e.target === e.currentTarget && openDropdown()"
        v-click-outside="closeDropdown"
    >
        <div
            v-if="isEmpty(value) && !props.isLoading"
            class="select-none"
            :class="{
                'text-red-600': isError,
            }"
            @click="openDropdown()"
        >
            {{ label }}
        </div>
        <div
            v-if="!isEmpty(value) && !props.isLoading"
            class="block w-[calc(100%-64px)] overflow-hidden text-ellipsis whitespace-nowrap"
            @click="openDropdown()"
        >
            {{ formatFunction(value) }}
        </div>
        <div
            v-if="props.isLoading"
            class="text-gray-400 select-none"
        >
            {{ props.isLoadingLabel }}
        </div>
        <div
            v-if="!props.isLoading"
            class="ml-auto"
            @click="openDropdown()"
        >
            <ArrowSvg />
        </div>
        <div
            v-else
            class="ml-auto"
            @click="openDropdown()"
        >
            <LoadingSvg class="size-4 animate-spin" />
        </div>
        <div
            class="text-xs absolute -top-2 left-5 bg-gray-100 px-2 rounded-full select-none"
            :class="{
                'text-red-600': isError && !isLoading,
                'hidden': isLoading,
            }"
        >
            {{ label }}
        </div>
        <div
            class="absolute z-40 left-0 w-full bg-white shadow-xl border border-gray-500 rounded-md overflow-hidden"
            :class="{ 'hidden': !isVisible }"
        >
            <div
                v-if="searchable"
                class="sticky top-0 z-10 bg-white border-b border-gray-200 p-2"
            >
                <input
                    ref="searchInputRef"
                    v-model="searchQuery"
                    type="text"
                    class="w-full h-9 px-3 text-sm border border-gray-200 rounded-md outline-none focus:border-blue-400"
                    :placeholder="searchPlaceholder"
                    @click.stop
                >
            </div>
            <div class="max-h-60 overflow-y-auto py-1">
                <div
                    v-for="(item, index) in filteredValues"
                    :key="item?.id ?? item?.criteria_type ?? index"
                    class="w-full h-8 flex items-center px-2 overflow-hidden text-ellipsis whitespace-nowrap hover:bg-gray-100 cursor-pointer"
                    @click="selectItem(item)"
                >
                    {{ formatFunction(item) }}
                </div>
                <div
                    v-if="!filteredValues.length"
                    class="px-2 py-2 text-sm text-gray-400"
                >
                    Ничего не найдено
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
